import buildUrl from 'modules/core/lib/buildUrl'
import type { IrisValueObjectsExportFormatEnum } from 'generated/resources/graphql'

import { datasourceTypeFromId } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

export type ExportReportFormat = IrisValueObjectsExportFormatEnum
export const MAX_EXPORT_FILE_NAME_LENGTH = 120

const exportExtension = /\.(csv|xlsx)$/i
const isUnsafeFileNameCharacter = (character: string) => {
  const code = character.charCodeAt(0)

  return code < 32 || code === 127 || character === '/' || character === '\\'
}

function bytesToBase64(bytes: Uint8Array) {
  const binString = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('')
  return btoa(binString)
}

export function sanitizeExportFileName(fileName: string) {
  return fileName
    .trim()
    .replace(exportExtension, '')
    .split('')
    .map((character) => (isUnsafeFileNameCharacter(character) ? ' ' : character))
    .join('')
    .replace(/\s+/g, ' ')
    .slice(0, MAX_EXPORT_FILE_NAME_LENGTH)
    .trim()
}

export const exportReport = (
  reportName: string,
  sqlQuery: string,
  companyId: string,
  datasourceId: string,
  dateFilter: { snapshotDate?: Date; dateRange?: { from: Date; to: Date } },
  filters?: string,
  columns?: string[] | null,
  columnAliases?: Record<string, string>,
  format: ExportReportFormat = 'csv'
) => {
  const sanitizedReportName = sanitizeExportFileName(reportName)
  const filename = sanitizedReportName ? `${sanitizedReportName}.${format}` : `export.${format}`
  const params = new URLSearchParams({
    sql_query: bytesToBase64(new TextEncoder().encode(sqlQuery ?? '')),
    company_id: companyId.toString(),
    table_id: datasourceId,
    filename,
    export_format: format,
  })
  const datasourceType = datasourceTypeFromId(datasourceId)
  if (datasourceType === 'snapshot') {
    params.set('snapshot_date', dateFilter.snapshotDate?.toISOString() ?? '')
  }
  if (datasourceType === 'traceability') {
    params.set('date_range[from]', dateFilter.dateRange?.from.toISOString() ?? '')
    params.set('date_range[to]', dateFilter.dateRange?.to.toISOString() ?? '')
  }
  if (filters) {
    params.set('filters', bytesToBase64(new TextEncoder().encode(filters)))
  }
  if (columns && columns.length > 0) {
    columns.forEach((col) => params.append('columns[]', col))
  }
  if (columnAliases) {
    Object.entries(columnAliases).forEach(([name, alias]) =>
      params.append(`column_aliases[${name}]`, alias)
    )
  }
  const url = buildUrl(`/iris/analytics_exports_${datasourceType}/download?${params.toString()}`)

  return fetch(url, { credentials: 'include' })
    .then((response) => {
      if (!response.ok) throw new Error(`Export failed: ${response.status}`)
      return response.blob()
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    })
    .catch(() => {
      window.alert('Export failed. Please try again.')
    })
}
